import type { FC } from 'react';
import { format, formatDistanceStrict } from 'date-fns/esm';
import { utcFormat } from 'd3-time-format';
import { scaleTime } from '@visx/scale';
import { useTheme } from '@simplywallst/ui-core';
import Label from '../Label';
import BuyLimit from '../BuyLimit';
import { Tooltip } from './styled';

interface Props {
  dividendNextDate: number;
  dividendNextPayDate: number;
}

const dateFormat = 'MMM dd yyyy';

const formatUTC = utcFormat('%b %d %Y');

export const NextDividend: FC<Props> = ({
  dividendNextDate,
  dividendNextPayDate,
}) => {
  const theme = useTheme();
  const now = Date.now();
  const xScale = scaleTime({
    range: [0, 1],
    domain: [now, dividendNextPayDate],
  });
  const dividendNextDateX = (xScale(dividendNextDate) as number) * 100;

  return (
    <svg height="224" width="100%" shapeRendering="crispEdges">
      <Label
        x={`${(xScale(now) as number) * 100}%`}
        y="168"
        title="Today"
        date={format(now, dateFormat)}
      />
      <Label
        x={`${dividendNextDateX}%`}
        y="0"
        title="Ex Dividend Date"
        date={formatUTC(new Date(dividendNextDate))}
        textAnchor={dividendNextDateX > 50 ? 'end' : 'start'}
        tooltip={() => (
          <Tooltip>
            You must be a shareholder of the company before this date to receive
            the next dividend payment
          </Tooltip>
        )}
      />
      <Label
        x={`${(xScale(dividendNextPayDate) as number) * 100}%`}
        y="168"
        highlight={true}
        title="Dividend Pay Date"
        date={formatUTC(new Date(dividendNextPayDate))}
        footer={`${formatDistanceStrict(dividendNextPayDate, dividendNextDate, {
          unit: 'day',
        })} from Ex Dividend`}
        textAnchor="end"
      />
      <svg
        y={theme.x5spacePx + 3}
        height={theme.x15spacePx}
        width="100%"
        shapeRendering="crispEdges"
      >
        <rect
          x="0"
          y={theme.x3spacePx}
          height={theme.x5spacePx}
          width="100%"
          fill={theme.color.chart03}
        />
        <rect
          x="0"
          y={theme.x3spacePx}
          height={theme.x5spacePx}
          width={`${(xScale(dividendNextDate) as number) * 100}%`}
          fill={theme.color.chart01}
        />
        <rect
          x="0"
          y={theme.x8spacePx}
          height={theme.x7spacePx}
          width="2"
          fill={theme.color.chart01}
        />
        <BuyLimit
          title={`Buy in the next ${formatDistanceStrict(
            dividendNextDate,
            now,
            {
              unit: 'day',
            }
          )} to receive the upcoming dividend`}
        />
        <rect
          x={`${(xScale(dividendNextDate) as number) * 100}%`}
          y="0"
          height={theme.x3spacePx}
          width="2"
          fill={theme.color.chart03}
        />
        <g transform="translate(-2, 0)">
          <rect
            x="100%"
            y={theme.x8spacePx}
            height={theme.x7spacePx}
            width="2"
            fill={theme.color.chart03}
          />
        </g>
      </svg>
    </svg>
  );
};

export default NextDividend;
